import React, { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import Table from "@mui/material/Table";
import Box from "@mui/material/Box";
import TableBody from "@mui/material/TableBody";

import CartItemsHeader from "../../shopping/Cart/CartItemsHeader";
import CartItemFields from "./CartItemFields";

export default function VirtualizedCartLocationTable({
  list,
  display,
  submitCounter,
  reFetch,
  removeInventoryItem,
  setFieldValue,
  inventoryItemsCount,
  disabled = false,
  centerAligned = false,
}) {
  const parentRef = useRef(null);

  const itemKeyFn = useMemo(() => {
    return index => {
      const item = list[index];
      return `${item?.id || index}`;
    };
  }, [list]);

  const rowVirtualizer = useVirtualizer({
    count: list.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    getItemKey: itemKeyFn,
    overscan: 10,
    lanes: 1,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const top = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const bottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  if (list.length < 100) {
    return (
      <>
        <Table>
          <CartItemsHeader display={display} centerAligned={centerAligned} />
          <TableBody>
            {list.map((item, index) => {
              const asset = item.asset;
              return (
                <CartItemFields
                  key={`${item.id}-${asset.id}`}
                  submitCounter={submitCounter}
                  inventoryItem={item}
                  display={display}
                  asset={asset}
                  reFetch={reFetch}
                  removeInventoryItem={removeInventoryItem}
                  setFieldValue={setFieldValue}
                  inventoryItemsCount={inventoryItemsCount}
                  disabled={disabled}
                  centerAligned={centerAligned}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        maxHeight: 600,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "auto",
        willChange: "scroll-position",
      }}
    >
      <Table>
        <CartItemsHeader display={display} centerAligned={centerAligned} />

        {top > 0 && (
          <tbody>
            <tr>
              <td colSpan="1000" style={{ height: top, padding: 0, border: 0 }} />
            </tr>
          </tbody>
        )}

        {virtualItems.map(virtualRow => {
          const item = list[virtualRow.index];
          const asset = item.asset;
          return (
            <tbody key={virtualRow.key} data-index={virtualRow.index}>
              <CartItemFields
                key={`${item.id}-${asset.id}`}
                submitCounter={submitCounter}
                inventoryItem={item}
                display={display}
                asset={asset}
                reFetch={reFetch}
                removeInventoryItem={removeInventoryItem}
                setFieldValue={setFieldValue}
                inventoryItemsCount={inventoryItemsCount}
                disabled={disabled}
                centerAligned={centerAligned}
              />
            </tbody>
          );
        })}
        {bottom > 0 && (
          <tbody>
            <tr>
              <td colSpan="1000" style={{ height: bottom, padding: 0, border: 0 }} />
            </tr>
          </tbody>
        )}
      </Table>
    </Box>
  );
}
